// SAM-624 — DISPLAY-ONLY price preview for the front-desk service booking.
//
// Mirrors sam-lambda-functions/functions/booking/pricing.py (compute_booking_amount) for the
// modes the guest-facing description endpoint exposes: per person (flat price, rate groups,
// tier-group `included`), unit (SAM-591), event size bands (SAM-592) and seasons (SAM-589).
// The order is the server's: season first, then the pricing mode, then add-ons.
//
// What it deliberately cannot know: the guest's role (prices_by_role) and what the guest already
// booked today (max_qty_per_day). The preview prices role-less lines at the flat price and the
// server stays the authority — hence the rail's "total confirmed by the server" note.
//
// NEVER used to build the request: the payload carries quantities, `rate_group` and
// `resource_key` only (CLAUDE.md, "Never send a price from the client").

import type { BookingOption } from '../../../hooks/useServices';
import { EVENT_SIZE_BANDS, eventSizeForPax, type EventSize } from '../../../utils/eventSize';
import type { PricingErrorCode } from '../../../utils/pricingErrors';
import { findSeasonForDate } from '../../../utils/pricingSeasons';
import type { BookingRateGroup, BookingService } from './bookingService';
import { labelOf } from './bookingService';

export type PreviewMode = 'person' | 'unit' | 'event';

export interface PreviewInput {
  service: Pick<BookingService, 'booking_options' | 'pricing' | 'rate_groups' | 'settings' | 'max_capacity'>;
  /** optionId -> quantity (0/absent = not requested). */
  quantities: Record<string, number>;
  /** optionId -> rate group id the operator picked. Absent = the server's default rule. */
  rateGroups?: Record<string, string>;
  /** Resource key the operator picked (unit mode). */
  resourceKey?: string | null;
  /** Booking start date, "YYYY-MM-DD". Absent = base prices. */
  bookingDate?: string | null;
  /** optionId -> qty for add-ons (priced at their flat price). */
  addOnQuantities?: Record<string, number>;
}

export type PreviewLineSource = 'flat' | 'rate_group' | 'unit_extra' | 'event_size';

export interface PreviewLine {
  optionId: string;
  label: string;
  quantity: number;
  /** Of `quantity`, how many are not charged (tier-group `included` or the unit's included_pax). */
  includedQuantity: number;
  unitPrice: number;
  total: number;
  source: PreviewLineSource;
  rateGroup?: string;
  eventSize?: EventSize;
}

export interface PreviewUnit {
  resourceKey: string | null;
  resourceName: string | null;
  price: number;
  includedPax: number;
  maxPax: number | null;
  /** People beyond `includedPax` (they pay extra_guest_price). */
  extraPax: number;
  pax: number;
}

export interface PreviewEvent {
  pax: number;
  band: EventSize;
  bandLabel: string;
  price: number;
}

export interface PreviewSeason {
  id: string;
  label: string;
}

export interface PreviewOk {
  ok: true;
  mode: PreviewMode;
  currency: string;
  total: number;
  lines: PreviewLine[];
  addOnsTotal: number;
  unit?: PreviewUnit;
  event?: PreviewEvent;
  season: PreviewSeason | null;
}

export interface PreviewError {
  ok: false;
  mode: PreviewMode;
  code: PricingErrorCode;
  details: Record<string, unknown>;
  season: PreviewSeason | null;
}

export type PreviewResult = PreviewOk | PreviewError;

// A type guard rather than `!preview.ok`: this project compiles without strictNullChecks, where
// TypeScript does not narrow a `boolean`-discriminated union on truthiness.
export const isPreviewError = (p: PreviewResult): p is PreviewError => p.ok === false;
export const isPreviewOk = (p: PreviewResult): p is PreviewOk => p.ok === true;

/** Errors the operator can fix before submitting; the selector blocks "Continue" on these. */
export const BLOCKING_PREVIEW_CODES: readonly PricingErrorCode[] = [
  'rate_group_required',
  'rate_group_cap_per_booking',
  'resource_required',
  'unit_max_pax_exceeded',
  'event_over_capacity',
];

class PreviewFail extends Error {
  constructor(public code: PricingErrorCode, public details: Record<string, unknown> = {}) {
    super(code);
  }
}

const round2 = (v: number): number => Math.round((v + Number.EPSILON) * 100) / 100;
const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const parsed = Number(v);
  return Number.isFinite(parsed) ? parsed : null;
};
const slug = (v: unknown): string => String(v ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

export function priceUnitOf(service: Pick<BookingService, 'pricing'> | null | undefined): PreviewMode {
  const unit = service?.pricing?.price_unit;
  return unit === 'unit' || unit === 'event' ? unit : 'person';
}

/** Rate groups price a service only in per-person mode (server: active_rate_groups). */
export function activeRateGroups(service: Pick<BookingService, 'pricing' | 'rate_groups'> | null | undefined): BookingRateGroup[] {
  if (!service || priceUnitOf(service) !== 'person') return [];
  return Array.isArray(service.rate_groups) ? service.rate_groups : [];
}

// ─── Seasons (SAM-589) ──────────────────────────────────────────────────────

type Seasons = NonNullable<NonNullable<PreviewInput['service']['pricing']>['seasons']>;
type Season = Seasons[number];

/**
 * The season containing `date`. Fixed seasons win over recurring ones (`every_year`, MM-DD bounds,
 * SAM-626), like the server's `find_season`; see utils/pricingSeasons.ts.
 */
export function findSeason(seasons: Seasons | undefined | null, date: string | null | undefined): Season | null {
  return findSeasonForDate(seasons, date);
}

const SCALAR_FIELDS = ['price', 'extra_guest_price'] as const;
const MAP_FIELDS = ['prices_by_group', 'prices_by_role', 'prices_by_event_size'] as const;

/** Scalars replace, maps merge per key; options the season does not list keep their base values. */
export function applySeason(options: BookingOption[], season: Season | null): BookingOption[] {
  if (!season) return options;
  return options.map((option) => {
    const override = season.prices?.[String(option.id)];
    if (!override || typeof override !== 'object') return option;
    const effective: Record<string, unknown> = { ...option };
    for (const field of SCALAR_FIELDS) {
      const value = (override as Record<string, unknown>)[field];
      if (value !== undefined && value !== null && value !== '') effective[field] = value;
    }
    for (const field of MAP_FIELDS) {
      const value = (override as Record<string, unknown>)[field];
      if (value && typeof value === 'object') {
        effective[field] = { ...((option as unknown as Record<string, object>)[field] || {}), ...(value as object) };
      }
    }
    return effective as unknown as BookingOption;
  });
}

// ─── Rate groups (SAM-588) ──────────────────────────────────────────────────

export function groupCell(option: BookingOption, groupId: string): number | null {
  return num(option.prices_by_group?.[String(groupId)]);
}

export function hasGroupPrices(option: BookingOption): boolean {
  return !!option.prices_by_group && Object.keys(option.prices_by_group).length > 0;
}

/** Groups that have a price for this option, in the service's group order. */
export function groupsForOption(option: BookingOption, groups: BookingRateGroup[]): BookingRateGroup[] {
  return groups.filter((g) => groupCell(option, g.id) !== null);
}

/**
 * The group the picker starts on: the `is_default` group when it has a cell, else the only group
 * with a cell, else null (the operator must choose). Same rule as pricing.py::_choose_rate_group.
 */
export function defaultGroupFor(option: BookingOption, groups: BookingRateGroup[]): string | null {
  const sellable = groupsForOption(option, groups);
  const defaults = sellable.filter((g) => g.is_default);
  if (defaults.length) return String(defaults[0].id);
  if (sellable.length === 1) return String(sellable[0].id);
  return null;
}

/** The group a line ends up priced in: the operator's pick when still valid, else the default. */
export function effectiveGroupFor(
  option: BookingOption,
  groups: BookingRateGroup[],
  picked: string | undefined
): string | null {
  if (picked && groups.some((g) => String(g.id) === picked)) return picked;
  return defaultGroupFor(option, groups);
}

// ─── Unit (SAM-591) ─────────────────────────────────────────────────────────

type Resource = NonNullable<NonNullable<PreviewInput['service']['settings']>['resources']>[number];

export function resourcesOf(service: Pick<BookingService, 'settings'> | null | undefined): Resource[] {
  const resources = service?.settings?.resources;
  return Array.isArray(resources) ? resources.filter((r) => r && typeof r === 'object') : [];
}

/** Whether the request must carry `resource_key`: unit mode with more than one resource. */
export function requiresResourceKey(service: Pick<BookingService, 'pricing' | 'settings'> | null | undefined): boolean {
  return priceUnitOf(service) === 'unit' && resourcesOf(service).length > 1;
}

export function resolveUnit(
  service: Pick<BookingService, 'pricing' | 'settings'>,
  resourceKey: string | null | undefined
): PreviewUnit {
  const base = { ...(service.pricing?.unit || {}) } as Record<string, unknown>;
  const resources = resourcesOf(service);
  let resource: Resource | null = null;
  if (resourceKey) {
    const wanted = slug(resourceKey);
    resource = resources.find((r) => wanted && (wanted === slug(r.key) || wanted === slug(r.name))) ?? null;
    if (!resource) throw new PreviewFail('resource_not_found', { resource_key: resourceKey });
  } else if (resources.length === 1) {
    resource = resources[0];
  } else if (resources.length > 1) {
    throw new PreviewFail('resource_required');
  }
  const override = resource?.metadata;
  if (override && typeof override === 'object') {
    for (const key of ['price', 'included_pax', 'max_pax'] as const) {
      const v = (override as Record<string, unknown>)[key];
      if (v !== undefined && v !== null && v !== '') base[key] = v;
    }
  }
  const price = num(base.price);
  if (price === null) throw new PreviewFail('unit_price_missing');
  const included = num(base.included_pax ?? 0) ?? 0;
  const maxPaxRaw = base.max_pax;
  const maxPax = maxPaxRaw === undefined || maxPaxRaw === null || maxPaxRaw === '' ? null : num(maxPaxRaw);
  if (price < 0 || included < 0 || (maxPaxRaw !== undefined && maxPaxRaw !== null && maxPaxRaw !== '' && (maxPax === null || maxPax < 1)) ||
      (maxPax !== null && included > maxPax)) {
    throw new PreviewFail('invalid_unit_config');
  }
  return {
    resourceKey: resource ? String(resource.key ?? resource.name ?? '') : null,
    resourceName: resource ? String(resource.name ?? resource.key ?? '') : null,
    price,
    includedPax: included,
    maxPax,
    extraPax: 0,
    pax: 0,
  };
}

/** {optionId: [includedUnits, extraUnits]}: included slots go to the highest extra_guest_price first (ties: option order). */
export function unitExtrasPlan(options: BookingOption[], quantities: Record<string, number>, includedPax: number) {
  const order = new Map(options.map((o, i) => [String(o.id), i] as const));
  const ranked = options
    .filter((o) => (quantities[o.id] ?? 0) > 0)
    .sort((a, b) => (num(b.extra_guest_price) ?? 0) - (num(a.extra_guest_price) ?? 0) || order.get(String(a.id))! - order.get(String(b.id))!);
  const plan: Record<string, [number, number]> = {};
  let slots = includedPax;
  for (const opt of ranked) {
    const qty = quantities[opt.id] ?? 0;
    const inc = Math.min(qty, slots);
    slots -= inc;
    plan[String(opt.id)] = [inc, qty - inc];
  }
  return plan;
}

// ─── Main entry ─────────────────────────────────────────────────────────────

export function previewBookingPrice(input: PreviewInput): PreviewResult {
  const { service } = input;
  const mode = priceUnitOf(service);
  const pricing = service.pricing || {};
  const season = findSeason(pricing.seasons, input.bookingDate);
  const seasonInfo: PreviewSeason | null = season
    ? { id: season.id, label: labelOf(season.label, season.id) }
    : null;

  try {
    const options = applySeason(service.booking_options || [], season);
    const requested = options.filter((o) => (input.quantities[o.id] ?? 0) > 0);
    const currency = requested[0]?.currency || options[0]?.currency || 'USD';
    const lines: PreviewLine[] = [];
    let total = 0;
    const pax = requested.reduce((sum, o) => sum + (input.quantities[o.id] ?? 0), 0);
    const result: Partial<PreviewOk> = {};

    if (mode === 'event') {
      if (pax > 0) {
        const cap = num(service.max_capacity);
        if (cap !== null && pax > cap) throw new PreviewFail('event_over_capacity', { requested: pax, max: cap });
        const band = eventSizeForPax(pax)!;
        let charged = false;
        for (const opt of requested) {
          const qty = input.quantities[opt.id] ?? 0;
          let price = 0;
          if (!charged) {
            const cell = num(opt.prices_by_event_size?.[band]);
            if (cell === null) throw new PreviewFail('event_size_price_missing', { option: opt.id, event_size: band });
            price = cell;
            charged = true;
            result.event = {
              pax, band,
              bandLabel: EVENT_SIZE_BANDS.find((b) => b.key === band)?.label || band,
              price,
            };
          }
          total += price;
          lines.push({ optionId: opt.id, label: opt.label_en || opt.id, quantity: qty, includedQuantity: 0, unitPrice: price, total: round2(price), source: 'event_size', eventSize: band });
        }
      }
    } else if (mode === 'unit') {
      const unit = resolveUnit(service, input.resourceKey);
      const plan = unitExtrasPlan(options, input.quantities, unit.includedPax);
      if (unit.maxPax !== null && pax > unit.maxPax) {
        throw new PreviewFail('unit_max_pax_exceeded', { max_pax: unit.maxPax, requested: pax });
      }
      for (const opt of requested) {
        const qty = input.quantities[opt.id] ?? 0;
        const [inc, extra] = plan[String(opt.id)] ?? [0, qty];
        const price = num(opt.extra_guest_price) ?? 0;
        const lineTotal = round2(price * extra);
        total += lineTotal;
        lines.push({ optionId: opt.id, label: opt.label_en || opt.id, quantity: qty, includedQuantity: inc, unitPrice: price, total: lineTotal, source: 'unit_extra' });
      }
      unit.pax = pax;
      unit.extraPax = Math.max(0, pax - unit.includedPax);
      total += round2(unit.price);
      result.unit = unit;
    } else {
      const groups = activeRateGroups(service);
      const groupQty: Record<string, number> = {};
      // tier-group `included` (older cabana model): free units go to the first tier first.
      const free: Record<string, number> = {};
      for (const tg of pricing.tier_groups || []) {
        let left = tg.included ?? 0;
        for (const id of tg.tiers || []) {
          const qty = input.quantities[id] ?? 0;
          const take = Math.min(qty, left);
          free[id] = (free[id] ?? 0) + take;
          left -= take;
        }
      }
      for (const opt of requested) {
        const qty = input.quantities[opt.id] ?? 0;
        let unitPrice: number;
        let source: PreviewLineSource = 'flat';
        let rateGroup: string | undefined;
        if (groups.length > 0 && hasGroupPrices(opt)) {
          const picked = input.rateGroups?.[opt.id];
          if (picked && !groups.some((g) => String(g.id) === picked)) throw new PreviewFail('unknown_rate_group', { rate_group: picked });
          const gid = picked || defaultGroupFor(opt, groups);
          if (!gid) throw new PreviewFail('rate_group_required', { option: opt.id });
          const cell = groupCell(opt, gid);
          if (cell === null) throw new PreviewFail('rate_group_not_sold', { option: opt.id, rate_group: gid });
          unitPrice = cell;
          source = 'rate_group';
          rateGroup = gid;
          groupQty[gid] = (groupQty[gid] ?? 0) + qty;
        } else {
          unitPrice = num(opt.price) ?? 0;
        }
        const includedQuantity = Math.min(free[opt.id] ?? 0, qty);
        const lineTotal = round2(unitPrice * (qty - includedQuantity));
        total += lineTotal;
        lines.push({ optionId: opt.id, label: opt.label_en || opt.id, quantity: qty, includedQuantity, unitPrice, total: lineTotal, source, ...(rateGroup ? { rateGroup } : {}) });
      }
      for (const [gid, qty] of Object.entries(groupQty)) {
        const cap = num(groups.find((g) => String(g.id) === gid)?.max_qty_per_booking);
        if (cap !== null && qty > cap) throw new PreviewFail('rate_group_cap_per_booking', { rate_group: gid, max: cap, requested: qty });
      }
    }

    let addOnsTotal = 0;
    for (const addOn of service.settings?.add_ons || []) {
      const qty = input.addOnQuantities?.[addOn.id] ?? 0;
      if (qty > 0) addOnsTotal += round2((num(addOn.price) ?? 0) * qty);
    }
    total += addOnsTotal;

    return { ok: true, mode, currency, total: round2(total), lines, addOnsTotal: round2(addOnsTotal), season: seasonInfo, ...result };
  } catch (err) {
    if (err instanceof PreviewFail) return { ok: false, mode, code: err.code, details: err.details, season: seasonInfo };
    throw err;
  }
}
